<template lang="pug">
//- v-if='checkoutData.summary'
div(class='relative')
  //- waiting spinner
  div(
    v-if='isWaiting'
    class='absolute flex justify-center top-0 bottom-0 h-full w-full z-20'
    style='background-color: rgba(0, 0, 0, 0.5); padding-top: 200px'
  )
    //- Style needed to override TW loader
    div(class='loader' style='width: 5em; height: 5em; border-width: 7px')

  div(
    class='order-info lg:border lg:ml-0 lg:mr-0'
    :class='isSimpleMode && $device.value.isMobile ? "bg-transparent" : "bg-white"'
  )
    div(
      class='uppercase text-black font-bold p-0 lg:border-b'
      :class='isSimpleMode && $device.value.isMobile ? "bg-white rounded p-3" : "p-0"'
    )
      div(class='flex justify-between py-3 lg:px-3')
        div Order Total
        div(
          v-if='$sitewideConfig.config.shareCartEnabled'
          class='save-cart relative text-right flex justify-end items-center text-action-dark'
        )
          button(class='no-underline text-xs' @click='$overlay.open("sc")')
            SaveIcon(class='fill-current align-middle mb-1 w-4 h-4')
            | &nbsp;Email Cart

          Tooltip(
            align='right'
            text='We\'ll email you a link to this cart so you can use it on another browser or device'
          )
            InfoIcon(class='fill-current align-middle ml-2 cursor-pointer mb-1 w-3 h-3')

    div(class='text-sm p-0 lg:p-3 lg:pb-1' :class='isSimpleMode && $device.value.isMobile ? "bg-white rounded p-3" : "p-0"')
      div(
        v-if='checkoutData.summary.cartSubtotal != checkoutData.summary.grandTotal'
        data-cypress='subtotal'
        class='flex justify-between font-bold'
      )
        div
          | Subtotal &nbsp;
          span(class='text-xs') ({{ numberize(checkoutData.summary.itemCount, 'item') }})

        div {{ formatCents(checkoutData.summary.cartSubtotal) }}

      div(data-cypress='shipping' class='flex justify-between')
        div Shipping
        div
          //- if we have a shipping subtotal and its truthy then its a number above 0
          template(v-if='checkoutData.summary.shippingSubtotal') {{ formatCents(checkoutData.summary.shippingSubtotal) }}
          //- Otherwise its free shipping
          template(v-else) Free Shipping

      div(
        v-if='checkoutData.summary.taxSubtotal || checkoutData.billingContact?.zipcode'
        data-cypress='tax'
        class='flex justify-between'
      )
        div Estimated Tax
        div {{ formatCents(checkoutData.summary.taxSubtotal) }}

      div(v-if='checkoutData.summary.giftCardSubtotal != 0' data-cypress='giftcardTotal' class='flex justify-between font-bold')
        div
          | Gift Card{{ plural(checkoutData.giftCards.length) }} &nbsp;
          a(href='#' @click.prevent='removeAllGiftCards(checkoutData.giftCards)')
            | Remove

        div(class='text-success') {{ formatCents(checkoutData.summary.giftCardSubtotal) }}

      div(data-cypress='total' class='flex justify-between font-bold text-black text-base mt-1 mb-4')
        div(class='py-3') {{ checkoutData.summary.taxSubtotal || (checkoutData.billingContact && checkoutData.billingContact.zipcode) ? 'Grand' : 'Estimated' }} Total
        div(class='py-3') {{ formatCents(checkoutData.summary.grandTotal) }}

      div(
        v-if='$route.name == "checkout-cart" && (isIntegrationEnabled("affirm") || isIntegrationEnabled("paypal"))'
        class='text-center border-t p-3'
        style='min-height: 70px'
      )
        AffirmAsLowAs(
          v-if='isIntegrationEnabled("affirm")'
          :price='checkoutData.summary.grandTotal'
          page-type='cart'
          class='text-left py- text-sm'
        )
        PaypalMessaging(
          v-if='isIntegrationEnabled("paypal")'
          :price='checkoutData.summary.grandTotal'
          page-type='cart'
        )

    div(
      v-if='$sitewideConfig.config.enableGiftCardInput'
      class='relative text-sm py-3 lg:mx-3'
      :class='isSimpleMode && $device.value.isMobile ? "bg-gray-lighter border-none px-0" : "border-t"'
    )
      a(class='collapsible-before' :class='{ open: showGiftCardForm }' @click.prevent='toggleGiftCardForm') &nbsp; Use a Gift Card
      div(v-if='showGiftCardForm' data-cypress='giftcards' class='mt-4')
        ul(v-if='checkoutData.giftCards[0]' class='my-2')
          li(v-for='giftcard in checkoutData.giftCards' :key='giftcard.token')
            | {{ giftcard.name }}
            span(class='pl-1 text-success') {{ formatCents(giftcard.price * -1) }}

        BaseForm(@submit='addGiftCard')
          BaseFormInput(name='giftCardToken' label='Card Number' type='text' :max-length='30' validation='required')
          BaseButton(type='submit' class='mt-3') Apply

    div(
      class='relative text-sm py-3 lg:mx-3'
      :class='isSimpleMode && $device.value.isMobile ? "bg-gray-lighter border-none px-0" : "border-t"'
    )
      a(class='collapsible-before' :class='{ open: showPromoCodeForm }' @click.prevent='togglePromoCodeForm') &nbsp; Add a Promo Code
      div(v-if='showPromoCodeForm' data-cypress='promocodes' class='mt-4')
        ul(v-if='checkoutData.promoCodes' class='my-2')
          li(v-for='promoCode in checkoutData.promoCodes' :key='promoCode.name') Code #[span(class='uppercase text-success font-bold') {{ promoCode.name }}] has been added.
            button(class='inline-block ml-3 font-bold text-action cursor-pointer' @click='removePromoCode(promoCode.name)') Remove
        div(class='mb-3') Enter a promo code to save and get discounts on your favorite products.

        BaseForm(@submit='addPromoCode')
          BaseFormInput(name='promoCode' label='Promo Code' type='text' :max-length='30' validation='required')
          div(v-if='promoCodeFormMessage') {{ promoCodeFormMessage }}
          BaseButton(type='submit' class='mt-3') Apply

    div(
      v-if='isIntegrationEnabled("cognito") && $device.value.isSmall && wishlistItemCount && $route.name == "checkout-cart"'
      class='relative text-sm py-3 lg:mx-3'
      :class='isSimpleMode && $device.value.isMobile ? "bg-gray-lighter border-none px-0" : "border-t"'
    )
      a(:class='{ open: showWishlist }' class='collapsible-before' @click.prevent='toggleWishlist') &nbsp; Saved Items ({{ wishlistItemCount }})
      div(v-if='showWishlist' data-cypress='wishlist' class='mt-4')
        Wishlist

    div(v-if='$route.name == "checkout-cart"' class='py-3 text-center border-t lg:mt-0 lg:mx-3 lg:mb-3')
      template(v-if='isIntegrationEnabled("cognito") && $cognito.isSalesAgent()')
        CheckoutPaylinkButton

      template(v-else)
        CookieAlsoSubscribe(class='text-gray-dark mt-4 mx-0 mb-5')
        BaseButton(size='LARGE' to='/checkout/address/' icon='PADLOCK' class='w-full' data-testid='checkoutButton') Checkout
        CheckoutThirdParty(v-if='checkoutData')

  div(v-if='$route.name != "checkout-cart" && $device.value.isDesktop')
    CheckoutTrust
</template>

<script>
import InfoIcon from '@/assets/info.svg?component'
import SaveIcon from '@/assets/save.svg?component'

export default defineNuxtComponent({
  components: {
    InfoIcon,
    SaveIcon,
  },
  data() {
    return {
      isWaiting: false,
      showGiftCardForm: false,
      showPromoCodeForm: false,
      promoCodeFormMessage: null,
      showWishlist: false,
    }
  },
  setup() {
    const { isIntegrationEnabled } = useUtils()
    const rootStore = useRootStore()
    const checkoutStore = useCheckoutStore()
    const notifications = useNotifications()
    const wishlist = useWishlist()

    return {
      wishlist,
      rootStore,
      checkoutStore,
      notifications,
      isIntegrationEnabled,
    }
  },
  computed: {
    isSimpleMode() {
      return this.rootStore.isSimpleMode
    },
    checkoutData() {
      return this.checkoutStore.checkout
    },
    wishlistItemCount() {
      return this.wishlist.data.value?.itemCount
    },
  },
  mounted() {
    if (this.checkoutData && this.checkoutData.promoCodes.length > 0) this.togglePromoCodeForm()
  },
  methods: {
    toggleGiftCardForm() {
      this.showGiftCardForm = !this.showGiftCardForm
      this.showPromoCodeForm = false
      this.showWishlist = false
    },
    async addGiftCard(data, form) {
      if (this.isWaiting) return
      this.isWaiting = true

      try {
        await this.checkoutStore.addGiftCard(data.giftCardToken)
        this.$uiEvents.$emit('giftCardAdded')

        form.reset()
      } catch (error) {
        this.$uiEvents.$emit('giftCardDenied')
        // https://github.com/AutoCustoms/uwp-api/blob/c0f8d98c76fedd1803e17177f9f2b5d5086a8149/src/routers/checkoutRouter.ts#L352
        // get error from payload??
        form.setErrors([], { giftCardToken: 'Unable to apply gift card' })
      } finally {
        this.isWaiting = false
      }
    },
    async removeAllGiftCards(giftCards) {
      if (this.isWaiting) return
      this.isWaiting = true

      try {
        for (let i = 0; i < giftCards.length; i++) {
          await this.checkoutStore.removeGiftCard(giftCards[i].token)
          this.$uiEvents.$emit('giftCardRemoved')
        }
      } catch (error) {
        this.notifications.addError('Error', 'Unable to remove gift card')
      } finally {
        this.isWaiting = false
        this.showGiftCardForm = false
      }
    },
    togglePromoCodeForm() {
      this.showPromoCodeForm = !this.showPromoCodeForm
      this.showGiftCardForm = false
      this.showWishlist = false
    },
    async addPromoCode(data, form) {
      // QQPAFIXQ -- good code
      // MDJ34TJY -- invalid code
      // 8OX8FNOT -- good code
      // N6YEPOL5 -- good code

      if (this.isWaiting) return
      this.isWaiting = true
      this.promoCodeFormMessage = null
      this.$uiEvents.$emit('promoCodeEntered', data.promoCode)
      try {
        const promoCodeResp = await this.checkoutStore.addPromoCode(data.promoCode)

        if (promoCodeResp.code === 'PROMO_CODE_NOT_APPLIED')
          this.promoCodeFormMessage = 'Promo code is now active, add the items to your cart for your discount.'

        form.reset()
      } catch (error) {
        let errorMessage = ''

        switch (error.data?.code) {
          case 'PROMO_CODE_EXISTS':
            errorMessage = 'Promo code cannot be applied.'
            break

          case 'SIMILAR_PROMO_CODE_EXISTS':
            errorMessage = 'Promo code cannot be applied.'

            break

          default:
            errorMessage = 'Promo code not valid.'
            break
        }

        form.setErrors([], { promoCode: errorMessage })
      } finally {
        this.isWaiting = false
      }
    },
    async removePromoCode(promoCode) {
      if (this.isWaiting) return

      this.isWaiting = true
      this.promoCodeFormMessage = null

      try {
        await this.checkoutStore.removePromoCode(promoCode)
      } catch (error) {
        this.notifications.addError('Error', 'Could not remove promo code.')
      } finally {
        this.isWaiting = false
      }
    },
    toggleWishlist() {
      this.showWishlist = !this.showWishlist
      this.showGiftCardForm = false
      this.showPromoCodeForm = false
    },
  },
})
</script>
